import React, { FunctionComponent, useContext, useState } from 'react';
import { PortfolioContext } from '../../contexts/portfolioContext';
import PortfolioItem from './PortfolioItem';
import PortfolioForm from './PortfolioForm';
import AddWorkModal from './AddWorkModal';
import { ReactComponent as IconFields } from '../../assets/svg/Icon-Fields.svg';
import { Portfolio } from '../../interfaces/user';

/**
 * PortfolioGrid
 * @param isOwner
 */

interface PortfolioGridProps {
    isOwner?: boolean;
}

const PortfolioGrid: FunctionComponent<PortfolioGridProps> = ({
    isOwner = false
}) => {
    const portfolios = useContext(PortfolioContext);
    const [showForm, setShowForm] = useState(false);
    const [showAddWorkModal, setShowAddWorkModal] = useState(false);
    const [editingPortfolio, setEditingPortfolio] = useState<Portfolio | null>(null);

    const handleFormSuccess = () => {
        setShowForm(false);
        setEditingPortfolio(null);
    };

    const handleFormCancel = () => {
        setShowForm(false);
        setEditingPortfolio(null);
    };

    const handleAddNew = () => {
        setEditingPortfolio(null);
        setShowAddWorkModal(true);
    };

    const handleEdit = (portfolio: Portfolio) => {
        setEditingPortfolio(portfolio);
        setShowForm(true);
    };

    const hasPortfolios = portfolios && portfolios.length > 0;
    const isFormVisible = showForm;

    return (
        <article className="tile is-child">
            <div className="portfolio-grid">
                {isOwner && !isFormVisible && hasPortfolios && (
                    <div className="portfolio-grid__header">
                        <h2 className="portfolio-grid__section-title">Your Portfolio</h2>
                        {hasPortfolios && (
                            <button
                                className="portfolio-grid__add-button"
                                onClick={handleAddNew}
                                title="Add Portfolio"
                            >
                                <IconFields />
                            </button>
                        )}
                    </div>
                )}

                {isFormVisible && (
                    <div className="portfolio-grid__form-wrapper">
                        <PortfolioForm
                            onSuccess={handleFormSuccess}
                            onCancel={handleFormCancel}
                            portfolio={editingPortfolio ?? undefined}
                        />
                    </div>
                )}

                {!isFormVisible && (
                    <>
                        {hasPortfolios ? (
                            <div className="portfolio-grid__items">
                                {portfolios.map((portfolio) => (
                                    <PortfolioItem
                                        key={portfolio.id}
                                        portfolio={portfolio}
                                        onEdit={isOwner ? handleEdit : undefined}
                                        isOwner={isOwner}
                                    />
                                ))}
                            </div>
                        ) : (
                            <article className="tile is-child page-user-profile-next">
                                <div className="card page-user-profile-next-card is-rounded is-dropshadowed">
                                    <header className="card-header page-user-profile-next-card-header header-padding has-text-centered is-block">
                                        <h1 className="has-text-weight-bold is-size-5">
                                            {isOwner ? 'No portfolio items yet' : 'No portfolio items'}
                                        </h1>
                                        <p className="is-small m-b-1">
                                            {isOwner
                                                ? 'Showcase your work by adding portfolio items'
                                                : 'This user hasn\'t added any portfolio items yet'}
                                        </p>
                                        {isOwner && (
                                            <button
                                                className="button is-size-regular is-primary"
                                                onClick={handleAddNew}
                                            >
                                                <span className="has-text-weight-bold is-small">
                                                    Add Portfolio
                                                </span>
                                            </button>
                                        )}
                                    </header>
                                    <div className="card-content is-paddingless is-rounded"></div>
                                </div>
                            </article>
                        )}
                    </>
                )}
            </div>

            <AddWorkModal
                isOpen={showAddWorkModal}
                onClose={() => setShowAddWorkModal(false)}
                onSuccess={() => {
                    setShowAddWorkModal(false);
                }}
            />
        </article>
    );
};

export default PortfolioGrid;